import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { CategoryTitleBar, AddCategoryPanel, countLabel } from '../../../Components/AdminEdit/CategoryTools';

/**
 * CombosSection — Clean, restaurant-style combo display.
 * When isEditing=true (admin/manager), shows inline CRUD controls.
 */
const CombosSection = ({
  combos, isEditing = false, canEditPrice = false, onUpdate, onMoveUp, onMoveDown,
}) => {
  const groups = useMemo(() => {
    if (!combos?.length) return [];
    const isGrouped = combos[0]?.cards !== undefined;
    return isGrouped
      ? combos
      : [{ group: 'Combos and Specials', icon: '🎁', subtitle: '', theme: 'amber', cards: combos }];
  }, [combos]);

  const [openGroups, setOpenGroups] = useState(() => new Set(groups.map((_, i) => i)));
  useEffect(() => {
    if (!groups.length) return;
    setOpenGroups(new Set(groups.map((_, i) => i)));
  }, [groups]);

  const toggle = useCallback((idx) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }, []);

  /* ── Admin edit helpers ── */
  const handleUpdateCard = useCallback((gi, ci, updated) => {
    const newGroups = groups.map((g, i) => {
      if (i !== gi) return g;
      return { ...g, cards: g.cards.map((c, j) => (j === ci ? updated : c)) };
    });
    onUpdate?.(newGroups[0]?.cards !== undefined ? newGroups : newGroups[0]?.cards);
  }, [groups, onUpdate]);

  const handleDeleteCard = useCallback((gi, ci) => {
    if (!window.confirm('Delete this combo?')) return;
    const newGroups = groups.map((g, i) => {
      if (i !== gi) return g;
      return { ...g, cards: g.cards.filter((_, j) => j !== ci) };
    });
    onUpdate?.(newGroups);
  }, [groups, onUpdate]);

  const handleMoveCard = useCallback((gi, ci, dir) => {
    const newGroups = groups.map((g, i) => {
      if (i !== gi) return g;
      const cards = [...g.cards];
      const to = ci + dir;
      if (to < 0 || to >= cards.length) return g;
      [cards[ci], cards[to]] = [cards[to], cards[ci]];
      return { ...g, cards };
    });
    onUpdate?.(newGroups);
  }, [groups, onUpdate]);

  /* ── Combo group (category) create / rename / delete / reorder ── */
  const handleAddGroup = useCallback(({ title, icon }) => {
    // Always emit the grouped shape: a menu still stored as a flat card list
    // has no place to hang a second group.
    onUpdate?.([...groups, { group: title, icon: icon || '🎁', subtitle: '', theme: 'amber', cards: [] }]);
  }, [groups, onUpdate]);

  const handleRenameGroup = useCallback((gi, name) => {
    onUpdate?.(groups.map((g, i) => (i === gi ? { ...g, group: name } : g)));
  }, [groups, onUpdate]);

  const handleDeleteGroup = useCallback((gi) => {
    onUpdate?.(groups.filter((_, i) => i !== gi));
  }, [groups, onUpdate]);

  const handleMoveGroup = useCallback((gi, dir) => {
    const next = [...groups];
    const to = gi + dir;
    if (to < 0 || to >= next.length) return;
    [next[gi], next[to]] = [next[to], next[gi]];
    onUpdate?.(next);
  }, [groups, onUpdate]);

  const handleAddCard = useCallback((gi) => {
    const newCard = { name: 'New Combo', badge: '🆕', desc: '', items: ['Item 1', 'Item 2'] };
    const newGroups = groups.map((g, i) => {
      if (i !== gi) return g;
      return { ...g, cards: [...g.cards, newCard] };
    });
    onUpdate?.(newGroups);
  }, [groups, onUpdate]);

  // Nothing to show a customer, but an editor still needs the "add group"
  // affordance — otherwise deleting the last group is a one-way door.
  if (!groups.length && !isEditing) return null;

  /* ── Admin Edit View ── */
  if (isEditing) {
    return (
      <div className="ae-section">
        {/* Combos is a category like any other in the running order, so it gets
            the same bar — minus rename and delete, which its shape doesn't
            support: it is a top-level key, not a section. */}
        {(onMoveUp || onMoveDown) && (
          <CategoryTitleBar
            title="Combos and Specials"
            hint={countLabel(groups.reduce((n, g) => n + g.cards.length, 0), 'combo')}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
          />
        )}
        {groups.map((group, gi) => (
          <div key={gi} className="ae-group">
            <CategoryTitleBar
              title={group.group}
              label="Combo group"
              hint={countLabel(group.cards.length, 'combo')}
              takenNames={groups.map((g) => g.group)}
              onRename={(name) => handleRenameGroup(gi, name)}
              onDelete={() => handleDeleteGroup(gi)}
              onMoveUp={gi > 0 ? () => handleMoveGroup(gi, -1) : undefined}
              onMoveDown={gi < groups.length - 1 ? () => handleMoveGroup(gi, 1) : undefined}
            />
            {group.cards.map((card, ci) => (
              <AdminComboCard
                key={ci}
                card={card}
                canEditPrice={canEditPrice}
                onSave={(updated) => handleUpdateCard(gi, ci, updated)}
                onDelete={() => handleDeleteCard(gi, ci)}
                onMoveUp={ci > 0 ? () => handleMoveCard(gi, ci, -1) : undefined}
                onMoveDown={ci < group.cards.length - 1 ? () => handleMoveCard(gi, ci, 1) : undefined}
              />
            ))}
            <button className="ae-add-btn" onClick={() => handleAddCard(gi)}>+ Add Combo</button>
          </div>
        ))}
        <AddCategoryPanel
          label="+ Add Combo Group"
          heading="New combo group"
          namePlaceholder="Group name — e.g. Lunch Specials"
          takenNames={groups.map((g) => g.group)}
          onCreate={handleAddGroup}
        />
      </div>
    );
  }

  /* ── Normal consumer view ── */
  return (
    <div className="fm-combos">
      {groups.map((group, gi) => {
        const isOpen = openGroups.has(gi);
        return (
          <div key={gi} className="fm-combos__group">
            <button
              className={`fm-combos__group-header${isOpen ? ' fm-combos__group-header--open' : ''}`}
              onClick={() => toggle(gi)}
            >
              <span className="fm-combos__group-icon">{group.icon}</span>
              <div className="fm-combos__group-info">
                <span className="fm-combos__group-name">{group.group}</span>
                {group.subtitle && <span className="fm-combos__group-sub">{group.subtitle}</span>}
              </div>
              <span className="fm-combos__group-count">{group.cards.length}</span>
              <span className="fm-combos__group-arrow">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </button>
            {isOpen && (
              <div className="fm-combos__list">
                {group.cards.map((card, ci) => (
                  <ComboItem key={ci} card={card} theme={group.theme} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── Admin combo card editor ── */
const AdminComboCard = ({ card, canEditPrice = false, onSave, onDelete, onMoveUp, onMoveDown }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(card);

  const handleSave = () => {
    const saved = { ...draft, items: draft.items };
    if (canEditPrice) {
      const p = parseFloat(draft.price);
      saved.price = isNaN(p) ? undefined : Math.round(p * 100) / 100;
    }
    onSave(saved);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="ae-combo-card">
        <div className="ae-form" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          <input className="ae-input" value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="Combo name" />
          <input className="ae-input" value={draft.desc || ''} onChange={(e) => setDraft(d => ({ ...d, desc: e.target.value }))} placeholder="Description" />
          <input className="ae-input" value={draft.badge || ''} onChange={(e) => setDraft(d => ({ ...d, badge: e.target.value }))} placeholder="Badge (emoji or text)" />
          {canEditPrice && (
            <input
              className="ae-input"
              type="number"
              min="0"
              step="0.01"
              value={draft.price != null ? String(draft.price) : ''}
              onChange={(e) => setDraft(d => ({ ...d, price: e.target.value }))}
              placeholder="Price (e.g. 9.99)"
            />
          )}
          <textarea
            className="ae-input"
            style={{ minHeight: 64, resize: 'vertical' }}
            value={Array.isArray(draft.items) ? draft.items.join('\n') : draft.items}
            onChange={(e) => setDraft(d => ({ ...d, items: e.target.value.split('\n').filter(Boolean) }))}
            placeholder="Items — one per line"
          />
          <div className="ae-form__btns">
            <button className="ae-btn-save" onClick={handleSave}>✓ Save</button>
            <button className="ae-btn-cancel" onClick={() => { setDraft(card); setEditing(false); }}>✕ Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ae-combo-card">
      <div className="ae-combo-card__header">
        <div className="ae-combo-card__title">{card.badge} {card.name}</div>
        {card.price != null && <span className="ae-row__price">${parseFloat(card.price).toFixed(2)}</span>}
        <div className="ae-row__btns">
          <button className="ae-icon-btn ae-icon-btn--move" onClick={onMoveUp}  disabled={!onMoveUp}  title="Move up">↑</button>
          <button className="ae-icon-btn ae-icon-btn--move" onClick={onMoveDown} disabled={!onMoveDown} title="Move down">↓</button>
          <button className="ae-icon-btn" onClick={() => { setDraft(card); setEditing(true); }} title="Edit">✏️</button>
          <button className="ae-icon-btn ae-icon-btn--del" onClick={onDelete} title="Delete">🗑️</button>
        </div>
      </div>
      {card.desc && <div className="ae-combo-card__desc">{card.desc}</div>}
      <div className="ae-combo-items">
        <strong>Includes: </strong>{Array.isArray(card.items) ? card.items.join(' · ') : card.items}
      </div>
    </div>
  );
};

/**
 * ComboItem — Compact combo row for consumers.
 */
const ComboItem = React.memo(({ card, theme }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <article
      className={`fm-combo${expanded ? ' fm-combo--open' : ''}`}
      onClick={() => setExpanded((p) => !p)}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((p) => !p); }}}
    >
      <div className="fm-combo__header">
        <span className={`fm-combo__badge fm-combo__badge--${theme}`}>{card.badge}</span>
        <span className="fm-combo__name">{card.name}</span>
        {card.price != null && (
          <span className="fm-combo__price">${card.price.toFixed(2)}</span>
        )}
        <span className={`fm-combo__count fm-combo__count--${theme}`}>{card.items.length} items</span>
        <span className={`fm-combo__toggle${expanded ? ' fm-combo__toggle--open' : ''}`}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>
      {expanded && (
        <div className="fm-combo__detail">
          {card.desc && <p className="fm-combo__desc">{card.desc}</p>}
          <div className="fm-combo__includes">
            {card.items.map((it, j) => (
              <span key={j} className={`fm-combo__tag fm-combo__tag--${theme}`}>{it}</span>
            ))}
          </div>
        </div>
      )}
    </article>
  );
});

export default React.memo(CombosSection);


/**
 * CombosSection — Clean, restaurant-style combo display.
 *
 * Design philosophy (matches Dosa V2 / SectionCard mobile list):
 * - Each combo group is a clean sub-section with a warm divider header
 * - Each combo shows name + description always visible (no hidden content)
 * - Items shown as an inline tag list — always visible, no expand needed
 * - Highlighted combos get a warm accent border + subtle "Popular" badge
 * - Tap any combo to see full detail in a smooth inline expansion
 */

